/**
 * Offerte Formulier - Nieuw/Bewerken
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { offertesService, OfferteCreate, OfferteRegel, OfferteCalculatie } from '../../services/offertes.service';
import { relationsService, Relation } from '../../services/relations.service';
import { botenService, BootDetail } from '../../services/boten.service';
import { voorraadService, VoorraadItem } from '../../services/voorraad.service';
import { bedrijfsgegevensService } from '../../services/bedrijfsgegevens.service';
import './Offertes.css';
import './OfferteDetail.css';
import '../relations/Relations.css';

interface OfferteFormProps {
  offerteId?: number;
}

const OfferteForm = ({ offerteId }: OfferteFormProps) => {
  const navigate = useNavigate();
  const isEditMode = !!offerteId;



  const [formData, setFormData] = useState<OfferteCreate>({
    relatie_id: 0,
    boot_id: undefined,
    offertenummer: '', // Added this line
    offerte_datum: new Date().toISOString().split('T')[0],
    geldig_tot: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 dagen geldig
    onderwerp: '',
    omschrijving: '',
    interne_notities: '',
    betalingstermijn: 30,
    betalingsvoorwaarden: '',
    btw_percentage: 21.00,
    subtotaal: 0.00,
    btw_bedrag: 0.00,
    totaal: 0.00,
    korting_percentage: 0.00,
    korting_bedrag: 0.00,
    uurloon: 0,
    marge_uitbesteed: 0,
    marge_anders: 0,
    actief: true,
    calculaties: [
      {
        volgnummer: 1,
        naam: 'Calculatie 1',
        omschrijving: '',
        subtotaal: 0,
        actief: true,
        regels: [
          {
            volgnummer: 1,
            type_regel: 'ARBEID',
            omschrijving: '',
            aantal: 1,
            eenheid: 'Uur',
            prijs_per_eenheid: 0,
            korting_percentage: 0,
            korting_bedrag: 0,
            totaal: 0,
            actief: true
          }
        ]
      }
    ],
    regels: [] // Backwards compatibility
  });

  // Huidige calculatie index
  const [currentCalculatieIndex, setCurrentCalculatieIndex] = useState(0);

  const [relaties, setRelaties] = useState<Relation[]>([]);
  const [boten, setBoten] = useState<BootDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relatieSearch, setRelatieSearch] = useState('');
  const [relatieDropdownOpen, setRelatieDropdownOpen] = useState(false);
  const [uurloonVerkoop, setUurloonVerkoop] = useState<number>(0);
  const [uurloonInkoop, setUurloonInkoop] = useState<number>(0);

  // Voorraad dropdown state
  const [voorraadItems, setVoorraadItems] = useState<VoorraadItem[]>([]);
  const [voorraadSearch, setVoorraadSearch] = useState<{[key: number]: string}>({});
  const [voorraadDropdownOpen, setVoorraadDropdownOpen] = useState<{[key: number]: boolean}>({});
  const [deleteCalculatieModal, setDeleteCalculatieModal] = useState<{show: boolean, index: number | null, naam: string}>({
    show: false,
    index: null,
    naam: ''
  });

  useEffect(() => {
    loadOptions();
    loadVoorraadItems();
    loadBedrijfsgegevens();
    if (isEditMode && offerteId) {
      loadOfferte(offerteId);
    }
  }, [isEditMode, offerteId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-dropdown')) {
        setRelatieDropdownOpen(false);
        setVoorraadDropdownOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (formData.relatie_id > 0) {
      loadBotenVoorRelatie(formData.relatie_id);
    }
  }, [formData.relatie_id]);

  const loadOptions = async () => {
    try {
      // Laad alle relaties (mogelijk meer dan 100)
      let allRelaties: Relation[] = [];
      let relatiesOffset = 0;
      let relatiesHasMore = true;

      while (relatiesHasMore) {
        const relatiesData = await relationsService.getAll({
          actief: true,
          limit: 100,
          offset: relatiesOffset,
          type: 'Klant'
        });
        allRelaties = [...allRelaties, ...relatiesData.items];
        relatiesHasMore = relatiesData.items.length === 100;
        relatiesOffset += 100;
      }

      setRelaties(allRelaties);
    } catch (err: any) {
      setError(err.message || 'Kon opties niet laden');
    }
  };

  const loadOfferte = async (id: number) => {
    try {
      setLoading(true);
      const data = await offertesService.getById(id);

      setFormData({
        relatie_id: data.relatie_id,
        boot_id: data.boot_id,
        offertenummer: data.offertenummer, // Added this line
        offerte_datum: data.offerte_datum,
        geldig_tot: data.geldig_tot,
        onderwerp: data.onderwerp,
        omschrijving: data.omschrijving || '',
        interne_notities: data.interne_notities || '',
        betalingstermijn: data.betalingstermijn,
        betalingsvoorwaarden: data.betalingsvoorwaarden || '',
        btw_percentage: data.btw_percentage,
        subtotaal: data.subtotaal,
        btw_bedrag: data.btw_bedrag,
        totaal: data.totaal,
        korting_percentage: data.korting_percentage,
        korting_bedrag: data.korting_bedrag,
        uurloon: data.uurloon || 0,
        marge_uitbesteed: data.marge_uitbesteed || 0,
        marge_anders: data.marge_anders || 0,
        actief: data.actief,
        calculaties: data.calculaties && data.calculaties.length > 0
          ? data.calculaties.map(calc => {
              const regels = calc.regels || [];
              const lastRegel = regels[regels.length - 1];
              if (!lastRegel || lastRegel.omschrijving) {
                return {
                  ...calc,
                  regels: [...regels, {
                    volgnummer: regels.length + 1,
                    type_regel: 'ARBEID',
                    omschrijving: '',
                    aantal: 1,
                    eenheid: 'Uur',
                    prijs_per_eenheid: 0,
                    korting_percentage: 0,
                    korting_bedrag: 0,
                    totaal: 0,
                    actief: true
                  }]
                };
              }
              return calc;
            })
          : [{
              volgnummer: 1,
              naam: 'Calculatie 1',
              omschrijving: '',
              subtotaal: 0,
              actief: true,
              regels: [{
                volgnummer: 1,
                type_regel: 'ARBEID',
                omschrijving: '',
                aantal: 1,
                eenheid: 'Uur',
                prijs_per_eenheid: 0,
                korting_percentage: 0,
                korting_bedrag: 0,
                totaal: 0,
                actief: true
              }]
            }],
        regels: data.regels || [] // Backwards compatibility
      });

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon offerte niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadBotenVoorRelatie = async (relatieId: number) => {
    try {
      const botenData = await botenService.getAll({
        eigenaar_id: relatieId,
        actief: true,
        limit: 100
      });
      setBoten(botenData);
    } catch (err: any) {
      console.error('Kon boten niet laden:', err);
      setBoten([]);
    }
  };

  const loadVoorraadItems = async () => {
    try {
      const items = await voorraadService.searchItems();
      setVoorraadItems(items);
    } catch (err: any) {
      console.error('Kon voorraad items niet laden:', err);
      setVoorraadItems([]);
    }
  };

  const loadBedrijfsgegevens = async () => {
    try {
      const gegevens = await bedrijfsgegevensService.get();
      // Converteer van centen naar euros
      setUurloonVerkoop(gegevens.uurloon_verkoop / 100);
      setUurloonInkoop(gegevens.uurloon_inkoop / 100);

      // Als het een nieuwe offerte is, vul standaardwaarden in
      if (!isEditMode) {
        const uurloon = gegevens.uurloon_verkoop / 100;
        setFormData(prev => {
          // Update bestaande ARBEID-regels met prijs 0 naar het uurloon
          const updatedCalculaties = (prev.calculaties || []).map(calc => ({
            ...calc,
            regels: (calc.regels || []).map(regel =>
              regel.type_regel === 'ARBEID' && (!regel.prijs_per_eenheid || regel.prijs_per_eenheid === 0)
                ? { ...regel, prijs_per_eenheid: uurloon, totaal: parseFloat((parseFloat(String(regel.aantal || 0)) * uurloon).toFixed(2)) }
                : regel
            )
          }));
          return {
            ...prev,
            uurloon: gegevens.uurloon_verkoop,
            marge_uitbesteed: 2500,
            marge_anders: 3500,
            calculaties: updatedCalculaties
          };
        });
      }
    } catch (err: any) {
      console.error('Kon bedrijfsgegevens niet laden:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculatie beheer functies
  const addCalculatie = () => {
    setFormData(prev => {
      const calculaties = prev.calculaties || [];
      const nieuwCalculatie: OfferteCalculatie = {
        volgnummer: calculaties.length + 1,
        naam: `Calculatie ${calculaties.length + 1}`,
        omschrijving: '',
        subtotaal: 0,
        actief: true,
        regels: []
      };
      return {
        ...prev,
        calculaties: [...calculaties, nieuwCalculatie]
      };
    });
    setCurrentCalculatieIndex(formData.calculaties?.length || 0);
  };

  const deleteCalculatie = (index: number) => {
    if ((formData.calculaties?.length || 0) <= 1) {
      alert('Je moet minimaal één calculatie hebben');
      return;
    }

    const calculatieNaam = formData.calculaties?.[index]?.naam || 'deze calculatie';
    setDeleteCalculatieModal({
      show: true,
      index: index,
      naam: calculatieNaam
    });
  };

  const confirmDeleteCalculatie = () => {
    if (deleteCalculatieModal.index === null) return;

    const index = deleteCalculatieModal.index;
    setFormData(prev => {
      const calculaties = prev.calculaties?.filter((_, i) => i !== index) || [];
      // Hernummer calculaties
      const renumbered = calculaties.map((calc, idx) => ({
        ...calc,
        volgnummer: idx + 1
      }));
      const totalen = berekenTotalen(renumbered);
      return {
        ...prev,
        ...totalen
      };
    });

    // Reset current index als nodig
    if (currentCalculatieIndex >= (formData.calculaties?.length || 1) - 1) {
      setCurrentCalculatieIndex(Math.max(0, currentCalculatieIndex - 1));
    }

    // Sluit modal
    setDeleteCalculatieModal({ show: false, index: null, naam: '' });
  };

  const updateCalculatieNaam = (index: number, naam: string) => {
    setFormData(prev => {
      const calculaties = [...(prev.calculaties || [])];
      calculaties[index] = { ...calculaties[index], naam };
      return { ...prev, calculaties };
    });
  };



  const deleteRegel = (regelIndex: number) => {
    setFormData(prev => {
      const calculaties = [...(prev.calculaties || [])];
      const regels = calculaties[currentCalculatieIndex].regels?.filter((_, i) => i !== regelIndex) || [];
      calculaties[currentCalculatieIndex] = {
        ...calculaties[currentCalculatieIndex],
        regels
      };

      const totalen = berekenTotalen(calculaties);
      return {
        ...prev,
        ...totalen
      };
    });
  };

  const moveRegelUp = (regelIndex: number) => {
    if (regelIndex === 0) return;

    setFormData(prev => {
      const calculaties = [...(prev.calculaties || [])];
      const regels = [...(calculaties[currentCalculatieIndex].regels || [])];

      // Wissel de regels om
      [regels[regelIndex - 1], regels[regelIndex]] = [regels[regelIndex], regels[regelIndex - 1]];

      // Update volgnummers
      regels.forEach((regel, i) => {
        regel.volgnummer = i + 1;
      });

      calculaties[currentCalculatieIndex] = {
        ...calculaties[currentCalculatieIndex],
        regels
      };

      return {
        ...prev,
        calculaties
      };
    });
  };

  const moveRegelDown = (regelIndex: number) => {
    setFormData(prev => {
      const calculaties = [...(prev.calculaties || [])];
      const regels = [...(calculaties[currentCalculatieIndex].regels || [])];

      if (regelIndex === regels.length - 1) return prev;

      // Wissel de regels om
      [regels[regelIndex], regels[regelIndex + 1]] = [regels[regelIndex + 1], regels[regelIndex]];

      // Update volgnummers
      regels.forEach((regel, i) => {
        regel.volgnummer = i + 1;
      });

      calculaties[currentCalculatieIndex] = {
        ...calculaties[currentCalculatieIndex],
        regels
      };

      return {
        ...prev,
        calculaties
      };
    });
  };

  const handleRegelUpdate = (regelIndex: number, field: keyof OfferteRegel, value: any) => {
    setFormData(prev => {
      const calculaties = JSON.parse(JSON.stringify(prev.calculaties || []));
      const currentRegels = calculaties[currentCalculatieIndex].regels || [];
      const updatedRegel = currentRegels[regelIndex];

      if (updatedRegel) {
        // Use a type assertion for the field
        (updatedRegel as any)[field] = value;

        // Als type_regel wordt gewijzigd naar ARBEID, vul automatisch het uurloon in
        if (field === 'type_regel' && value === 'ARBEID' && uurloonVerkoop > 0) {
          updatedRegel.prijs_per_eenheid = uurloonVerkoop;
          updatedRegel.eenheid = 'Uur';
        }

        // Bij lege ARBEID-regel: vul uurloon in zodra er getypt wordt
        if (updatedRegel.type_regel === 'ARBEID' && updatedRegel.prijs_per_eenheid === 0 && uurloonVerkoop > 0) {
          if (field === 'omschrijving' || field === 'aantal') {
            updatedRegel.prijs_per_eenheid = uurloonVerkoop;
            if (field === 'aantal' || !updatedRegel.aantal) {
              updatedRegel.aantal = updatedRegel.aantal || 1;
            }
          }
        }

        // Recalculate total for the updated rule
        const aantal = parseFloat(String(updatedRegel.aantal || 0));
        const prijs = parseFloat(String(updatedRegel.prijs_per_eenheid || 0));
        let totaal = aantal * prijs;

        if (updatedRegel.korting_percentage && parseFloat(String(updatedRegel.korting_percentage)) > 0) {
          totaal -= totaal * (parseFloat(String(updatedRegel.korting_percentage)) / 100);
        } else if (updatedRegel.korting_bedrag && parseFloat(String(updatedRegel.korting_bedrag)) > 0) {
          totaal -= parseFloat(String(updatedRegel.korting_bedrag));
        }
        updatedRegel.totaal = parseFloat(totaal.toFixed(2));

        // Check of automatisch een nieuwe regel moet worden toegevoegd
        const isRegelCompleet = updatedRegel.omschrijving && updatedRegel.aantal > 0 && updatedRegel.prijs_per_eenheid > 0;
        const isLaatsteRegel = regelIndex === currentRegels.length - 1;
        
        if (isRegelCompleet && isLaatsteRegel) {
          // Voeg automatisch een nieuwe lege regel toe
          currentRegels.push({
            volgnummer: currentRegels.length + 1,
            type_regel: 'ARBEID',
            omschrijving: '',
            aantal: 1,
            eenheid: 'Uur',
            prijs_per_eenheid: 0,
            korting_percentage: 0,
            korting_bedrag: 0,
            totaal: 0,
            actief: true
          });
          calculaties[currentCalculatieIndex].regels = currentRegels;
        }
        
        const totalen = berekenTotalen(calculaties);
        return {
          ...prev,
          ...totalen
        };
      }
      return prev;
    });
  };

  const berekenTotalen = (calculaties: OfferteCalculatie[]) => {
    // Bereken subtotaal per calculatie
    const updatedCalculaties = calculaties.map(calc => {
      const calcSubtotaal = (calc.regels || []).reduce((sum, regel) => {
        const regelTotaal = typeof regel.totaal === 'number' ? regel.totaal : parseFloat(String(regel.totaal || 0));
        return sum + regelTotaal;
      }, 0);
      return {
        ...calc,
        subtotaal: parseFloat(calcSubtotaal.toFixed(2))
      };
    });

    // Bereken totale subtotaal van alle calculaties
    const subtotaal = updatedCalculaties.reduce((sum, calc) => {
      const calcSub = typeof calc.subtotaal === 'number' ? calc.subtotaal : parseFloat(String(calc.subtotaal || 0));
      return sum + calcSub;
    }, 0);
    const btw_bedrag = subtotaal * ((formData.btw_percentage || 21) / 100);
    const totaal = subtotaal + btw_bedrag;

    return {
      calculaties: updatedCalculaties,
      subtotaal: parseFloat(subtotaal.toFixed(2)),
      btw_bedrag: parseFloat(btw_bedrag.toFixed(2)),
      totaal: parseFloat(totaal.toFixed(2))
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.relatie_id === 0) {
      setError('Selecteer een klant');
      return;
    }

    if (!formData.onderwerp) {
      setError('Voer een onderwerp in');
      return;
    }

    // Check of er minimaal één regel in een calculatie zit
    const hasRegels = formData.calculaties?.some(calc => (calc.regels?.length || 0) > 0);
    if (!hasRegels) {
      setError('Voeg minimaal één regel toe aan een calculatie');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        await offertesService.update(offerteId, formData);
        navigate(`/offertes/${offerteId}`);
      } else {
        const newOfferte = await offertesService.create(formData);
        navigate(`/offertes/${newOfferte.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const berekenWinstmarges = () => {
    let winstMargeMaterialiaal = 0;
    let winstMargeUren = 0;
    let winstMargeUitbesteed = 0;

    formData.calculaties?.forEach(calc => {
      calc.regels?.forEach(regel => {
        // Alleen regels meenemen die een omschrijving hebben (= ingevulde regels)
        if (!regel.omschrijving || regel.omschrijving.trim() === '') {
          return;
        }

        if (regel.type_regel === 'ARBEID') {
          // Winstmarge op uren = (uurloon_offerte - uurloon_inkoop) * aantal uren
          // Gebruik uurloon uit offerte (verkoop) en uurloon_inkoop uit bedrijfsgegevens
          const aantalUren = parseFloat(String(regel.aantal || 0));
          const uurloonOfferte = formData.uurloon / 100; // Van centen naar euros
          winstMargeUren += (uurloonOfferte - uurloonInkoop) * aantalUren;
        } else if (regel.type_regel === 'ONDERDEEL') {
          const aantal = parseFloat(String(regel.aantal || 0));
          const verkoopprijs = parseFloat(String(regel.prijs_per_eenheid || 0));

          // Zoek het voorraad item op
          const voorraadItem = voorraadItems.find(item => item.id === regel.voorraad_item_id);

          if (voorraadItem && voorraadItem.inkoopprijs !== null && voorraadItem.inkoopprijs > 0) {
            // Als inkoopprijs bekend is: winstmarge = (verkoop - inkoop) * aantal
            const inkoopprijs = voorraadItem.inkoopprijs;
            winstMargeMaterialiaal += (verkoopprijs - inkoopprijs) * aantal;
          } else {
            // Geen inkoopprijs bekend: gebruik marge_anders percentage
            const margeAnders = formData.marge_anders / 100; // Van centen naar percentage (bijv. 3500 -> 35%)
            const totaal = verkoopprijs * aantal;
            winstMargeMaterialiaal += totaal * (margeAnders / 100);
          }
        } else if (regel.type_regel === 'UITBESTEED') {
          // Voor uitbesteed werk: gebruik marge_uitbesteed percentage
          const totaal = parseFloat(String(regel.totaal || 0));
          const margeUitbesteed = formData.marge_uitbesteed / 100; // Van centen naar percentage
          winstMargeUitbesteed += totaal * (margeUitbesteed / 100);
        }
      });
    });

    return {
      materiaal: winstMargeMaterialiaal,
      uren: winstMargeUren,
      uitbesteed: winstMargeUitbesteed
    };
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const selectedRelatie = relaties.find(r => r.id === formData.relatie_id);
  const filteredRelaties = relatieSearch
    ? relaties.filter(r =>
        r.bedrijfsnaam.toLowerCase().includes(relatieSearch.toLowerCase()) ||
        (r.voornaam && r.voornaam.toLowerCase().includes(relatieSearch.toLowerCase())) ||
        (r.achternaam && r.achternaam.toLowerCase().includes(relatieSearch.toLowerCase()))
      )
    : relaties;

  if (loading && isEditMode) {
    return (
      <div className="offerte-form-container">
        <div className="loading">Offerte laden...</div>
      </div>
    );
  }

  return (
    <div className="offerte-form-container">
      <div className="page-header">
        <h1>{isEditMode ? 'Offerte Bewerken' : 'Nieuwe Offerte'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate(isEditMode ? `/offertes/${offerteId}` : '/offertes')}>
          {isEditMode ? 'Annuleren' : 'Annuleren'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>Klantgegevens</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Klant *</label>
              <div className="searchable-dropdown">
                <input
                  type="text"
                  value={relatieDropdownOpen
                    ? relatieSearch
                    : (selectedRelatie
                        ? (selectedRelatie.bedrijfsnaam === 'Particulier'
                            ? (() => {
                                const naamParts = [
                                  selectedRelatie.achternaam,
                                  selectedRelatie.tussenvoegsel ? `${selectedRelatie.voornaam} ${selectedRelatie.tussenvoegsel}` : selectedRelatie.voornaam
                                ].filter(Boolean);
                                return naamParts.join(', ');
                              })()
                            : selectedRelatie.bedrijfsnaam)
                        : relatieSearch)}
                  onChange={(e) => setRelatieSearch(e.target.value)}
                  onFocus={() => {
                    setRelatieDropdownOpen(true);
                    setRelatieSearch('');
                  }}
                  placeholder="Zoek klant..."
                  required
                  autoComplete="off"
                />
                {relatieDropdownOpen && (
                  <div className="dropdown-list">
                    {filteredRelaties.map(relatie => {
                      let displayName = relatie.bedrijfsnaam;
                      if (relatie.bedrijfsnaam === 'Particulier') {
                        const naamParts = [
                          relatie.achternaam,
                          relatie.tussenvoegsel ? `${relatie.voornaam} ${relatie.tussenvoegsel}` : relatie.voornaam
                        ].filter(Boolean);
                        displayName = naamParts.join(', ');
                      }
                      return (
                        <div
                          key={relatie.id}
                          className="dropdown-item"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, relatie_id: relatie.id }));
                            setRelatieSearch('');
                            setRelatieDropdownOpen(false);
                          }}
                        >
                          {displayName}
                        </div>
                      );
                    })}
                    {filteredRelaties.length === 0 && (
                      <div className="dropdown-item disabled">Geen klanten gevonden</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Boot (optioneel)</label>
              <select
                name="boot_id"
                value={formData.boot_id || ''}
                onChange={handleChange}
              >
                <option value="">Geen boot geselecteerd</option>
                {boten.map(boot => (
                  <option key={boot.id} value={boot.id}>
                    {boot.bootnaam || boot.boot_model.model}{boot.registratienummer ? ` (${boot.registratienummer})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Offerte Details</h2>

          <div className="form-group">
            <label>Onderwerp *</label>
            <input
              type="text"
              name="onderwerp"
              value={formData.onderwerp}
              onChange={handleChange}
              placeholder="Bijv: Onderhoud boot 2024"
              required
            />
          </div>

          <div className="form-group">
            <label>Omschrijving</label>
            <textarea
              name="omschrijving"
              value={formData.omschrijving}
              onChange={handleChange}
              rows={4}
              placeholder="Algemene omschrijving van de werkzaamheden..."
            />
          </div>

          <div className="form-group">
            <label>Interne Notities (niet zichtbaar voor klant)</label>
            <textarea
              name="interne_notities"
              value={formData.interne_notities}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr auto', gap: '15px', alignItems: 'start' }}>
            <div className="form-group">
              <label>Uurloon (€)</label>
              <input
                type="number"
                name="uurloon"
                value={formData.uurloon / 100}
                onChange={(e) => {
                  const euros = parseFloat(e.target.value) || 0;
                  setFormData(prev => ({ ...prev, uurloon: Math.round(euros * 100) }));
                }}
                step="0.01"
                min="0"
                placeholder="75.00"
              />
            </div>

            <div className="form-group">
              <label>Marge uitbesteed (%)</label>
              <input
                type="number"
                name="marge_uitbesteed"
                value={formData.marge_uitbesteed / 100}
                onChange={(e) => {
                  const percentage = parseFloat(e.target.value) || 0;
                  setFormData(prev => ({ ...prev, marge_uitbesteed: Math.round(percentage * 100) }));
                }}
                step="0.01"
                min="0"
                max="100"
                placeholder="15.00"
              />
            </div>

            <div className="form-group">
              <label>Marge anders (%)</label>
              <input
                type="number"
                name="marge_anders"
                value={formData.marge_anders / 100}
                onChange={(e) => {
                  const percentage = parseFloat(e.target.value) || 0;
                  setFormData(prev => ({ ...prev, marge_anders: Math.round(percentage * 100) }));
                }}
                step="0.01"
                min="0"
                max="100"
                placeholder="10.00"
              />
            </div>

            <div style={{ marginTop: '24px' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  // Update alle ARBEID regels met het nieuwe uurloon
                  setFormData(prev => {
                    const nieuweCalculaties = prev.calculaties?.map(calc => ({
                      ...calc,
                      regels: calc.regels?.map(regel => {
                        if (regel.type_regel === 'ARBEID') {
                          const nieuwUurloon = prev.uurloon / 100; // Van centen naar euros
                          const aantal = parseFloat(String(regel.aantal || 0));
                          let totaal = aantal * nieuwUurloon;

                          // Pas korting toe indien aanwezig
                          if (regel.korting_percentage && parseFloat(String(regel.korting_percentage)) > 0) {
                            totaal -= totaal * (parseFloat(String(regel.korting_percentage)) / 100);
                          } else if (regel.korting_bedrag && parseFloat(String(regel.korting_bedrag)) > 0) {
                            totaal -= parseFloat(String(regel.korting_bedrag));
                          }

                          return {
                            ...regel,
                            prijs_per_eenheid: nieuwUurloon,
                            totaal: parseFloat(totaal.toFixed(2))
                          };
                        }
                        return regel;
                      })
                    }));

                    return { ...prev, calculaties: nieuweCalculaties };
                  });
                }}
              >
                Herbereken
              </button>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Calculaties</h2>
            <button type="button" className="btn btn-sm btn-success" onClick={addCalculatie}>
              + Nieuwe Calculatie
            </button>
          </div>

          {/* Calculatie tabs */}
          <div className="calculatie-tabs">
            {formData.calculaties?.map((calc, index) => (
              <div
                key={index}
                className={`calculatie-tab ${currentCalculatieIndex === index ? 'active' : ''}`}
                onClick={() => setCurrentCalculatieIndex(index)}
              >
                <span className="calculatie-tab-naam">{calc.naam}</span>
                {formData.calculaties && formData.calculaties.length > 1 && (
                  <button
                    type="button"
                    className="delete-calculatie-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteCalculatie(index);
                    }}
                    title="Verwijder calculatie"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Huidige calculatie inhoud */}
          {formData.calculaties && formData.calculaties[currentCalculatieIndex] && (
            <div className="calculatie-content">
              {/* Calculatie naam en omschrijving */}
              <div className="calculatie-header-form">
                <div className="form-group">
                  <label>Calculatie Naam</label>
                  <input
                    type="text"
                    value={formData.calculaties[currentCalculatieIndex].naam}
                    onChange={(e) => updateCalculatieNaam(currentCalculatieIndex, e.target.value)}
                    placeholder="bijv. Romp reparatie, Motor onderhoud..."
                    className="calculatie-naam-field"
                  />
                </div>
                <div className="form-group">
                  <label>Omschrijving (optioneel)</label>
                  <textarea
                    value={formData.calculaties[currentCalculatieIndex].omschrijving || ''}
                    onChange={(e) => {
                      setFormData(prev => {
                        const calculaties = [...(prev.calculaties || [])];
                        calculaties[currentCalculatieIndex] = {
                          ...calculaties[currentCalculatieIndex],
                          omschrijving: e.target.value
                        };
                        return { ...prev, calculaties };
                      });
                    }}
                    rows={2}
                    placeholder="Korte beschrijving van deze calculatie..."
                  />
                </div>
              </div>

              {/* Regel toevoegen knop */}
              <div className="add-regel-section">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Add regel clicked, currentCalculatieIndex:', currentCalculatieIndex);
                    console.log('formData.calculaties:', formData.calculaties);
                    const newCalculaties = [...(formData.calculaties || [])];
                    const currentRegels = newCalculaties[currentCalculatieIndex]?.regels || [];
                    newCalculaties[currentCalculatieIndex].regels = [...currentRegels, {
                      volgnummer: currentRegels.length + 1,
                      type_regel: 'ARBEID',
                      omschrijving: '',
                      aantal: 1,
                      eenheid: 'Uur',
                      prijs_per_eenheid: 0,
                      korting_percentage: 0,
                      korting_bedrag: 0,
                      totaal: 0,
                      actief: true
                    }];
                    setFormData(prev => ({
                      ...prev,
                      calculaties: newCalculaties
                    }));
                  }}
                  className="btn btn-secondary"
                  style={{ marginTop: '10px' }}
                >
                  + Regel toevoegen
                </button>
              </div>

              {/* Regels tabel */}
              {formData.calculaties[currentCalculatieIndex].regels && formData.calculaties[currentCalculatieIndex].regels.length > 0 && (
                <div className="regels-list">
                  <table className="regels-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th style={{ width: '400px' }}>Omschrijving</th>
                        <th style={{ width: '80px' }}>Aantal</th>
                        <th>Eenheid</th>
                        <th style={{ width: '100px' }}>Prijs</th>
                        <th>Totaal</th>
                        <th>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.calculaties[currentCalculatieIndex].regels.map((regel, index) => (
                        <tr key={index}>
                          <td>{regel.volgnummer}</td>
                          <td>
                            <select
                              name="type_regel"
                              value={regel.type_regel}
                              onChange={(e) => handleRegelUpdate(index, 'type_regel', e.target.value)}
                              className="form-control-inline"
                              style={{ border: 'none', outline: 'none' }}
                            >
                              <option value="ARBEID">Arbeid</option>
                              <option value="ONDERDEEL">Voorraad</option>
                              <option value="UITBESTEED">Uitbesteed</option>
                              <option value="TEKST">Anders</option>
                            </select>
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '6px', verticalAlign: 'top', fontSize: '9pt', width: '400px' }}>
                            {regel.type_regel === 'ONDERDEEL' ? (
                              <div className="searchable-dropdown" style={{ position: 'relative', width: '100%' }}>
                                <input
                                  type="text"
                                  placeholder="Zoek voorraad item..."
                                  value={voorraadSearch[index] !== undefined ? voorraadSearch[index] : regel.omschrijving}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setVoorraadSearch(prev => ({ ...prev, [index]: newValue }));
                                    handleRegelUpdate(index, 'omschrijving', newValue);
                                    setVoorraadDropdownOpen(prev => ({ ...prev, [index]: true }));
                                  }}
                                  onFocus={() => setVoorraadDropdownOpen(prev => ({ ...prev, [index]: true }))}
                                  className="form-control-inline"
                                  style={{ width: '100%', border: 'none', outline: 'none' }}
                                  autoComplete="off"
                                />
                                {voorraadDropdownOpen[index] && (
                                  <div className="dropdown-list" style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    zIndex: 1000,
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                  }}>
                                    {voorraadItems
                                      .filter(item => {
                                        const search = (voorraadSearch[index] || '').toLowerCase();
                                        if (!search) return true;

                                        return (item.onderdeel_type || '').toLowerCase().includes(search) ||
                                          (item.model || '').toLowerCase().includes(search);
                                      })
                                      .slice(0, 500)
                                      .map(item => (
                                        <div
                                          key={item.id}
                                          onClick={() => {
                                            const displayText = `${item.onderdeel_type || ''} ${item.model || ''}`.trim();
                                            handleRegelUpdate(index, 'omschrijving', displayText);
                                            handleRegelUpdate(index, 'voorraad_item_id', item.id);
                                            if (item.verkoopprijs) {
                                              handleRegelUpdate(index, 'prijs_per_eenheid', item.verkoopprijs);
                                            }
                                            setVoorraadSearch(prev => ({ ...prev, [index]: displayText }));
                                            setVoorraadDropdownOpen(prev => ({ ...prev, [index]: false }));
                                          }}
                                          style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f0f0f0'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                          {item.onderdeel_type || ''} {item.model || ''}
                                        </div>
                                      ))}
                                    {voorraadItems.filter(item => {
                                      const search = (voorraadSearch[index] || '').toLowerCase();
                                      if (!search) return true;

                                      return (item.onderdeel_type || '').toLowerCase().includes(search) ||
                                        (item.model || '').toLowerCase().includes(search);
                                    }).length === 0 && (
                                      <div style={{ padding: '12px', color: '#999', textAlign: 'center' }}>
                                        Geen items gevonden
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <input
                                type="text"
                                name="omschrijving"
                                value={regel.omschrijving}
                                onChange={(e) => handleRegelUpdate(index, 'omschrijving', e.target.value)}
                                className="form-control-inline"
                                style={{ width: '100%', border: 'none', outline: 'none' }}
                              />
                            )}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '6px', verticalAlign: 'top', fontSize: '9pt', width: '80px' }}>
                            <input
                              type="number"
                              name="aantal"
                              value={regel.aantal}
                              onChange={(e) => handleRegelUpdate(index, 'aantal', parseFloat(e.target.value))}
                              step="0.01"
                              min="0"
                              className="form-control-inline"
                              style={{ width: '70px', border: 'none', outline: 'none' }}
                            />
                          </td>
                          <td>
                            <select
                              name="eenheid"
                              value={regel.eenheid}
                              onChange={(e) => handleRegelUpdate(index, 'eenheid', e.target.value)}
                              disabled={regel.type_regel === 'ARBEID'}
                              className="form-control-inline"
                              style={{ border: 'none', outline: 'none' }}
                            >
                              {regel.type_regel === 'ARBEID' ? (
                                <option value="Uur">Uur</option>
                              ) : (
                                <>
                                  <option value="Stuks">Stuks</option>
                                  <option value="Liter">Liter</option>
                                  <option value="Meter">Meter</option>
                                  <option value="m²">m²</option>
                                  <option value="kg">kg</option>
                                  <option value="Set">Set</option>
                                  <option value="Pak">Pak</option>
                                  <option value="Doos">Doos</option>
                                </>
                              )}
                            </select>
                          </td>
                          <td style={{ width: '100px' }}>
                            <input
                              type="number"
                              name="prijs_per_eenheid"
                              value={regel.prijs_per_eenheid}
                              onChange={(e) => handleRegelUpdate(index, 'prijs_per_eenheid', parseFloat(e.target.value))}
                              step="0.01"
                              min="0"
                              className="form-control-inline"
                              style={{ width: '90px', border: 'none', outline: 'none' }}
                            />
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}><strong>{formatCurrency(regel.totaal)}</strong></td>
                          <td>
                            <div className="regel-actions">
                              <div className="move-buttons">
                                <button
                                  type="button"
                                  className="btn-move-up"
                                  onClick={() => moveRegelUp(index)}
                                  disabled={index === 0}
                                  title="Omhoog"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="btn-move-down"
                                  onClick={() => moveRegelDown(index)}
                                  disabled={index === (formData.calculaties?.[currentCalculatieIndex]?.regels?.length || 0) - 1}
                                  title="Omlaag"
                                >
                                  ↓
                                </button>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteRegel(index)}
                              >
                                Verwijderen
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Calculatie subtotaal */}
                  <div className="calculatie-subtotaal">
                    <strong>Subtotaal {formData.calculaties[currentCalculatieIndex].naam}:</strong>
                    <strong>{formatCurrency(formData.calculaties[currentCalculatieIndex].subtotaal || 0)}</strong>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-section totalen-section">
          <div className="totalen">
            <div className="totaal-row">
              <span>Subtotaal:</span>
              <strong>{formatCurrency(formData.subtotaal || 0)}</strong>
            </div>
            <div className="totaal-row">
              <span>BTW ({formData.btw_percentage}%):</span>
              <strong>{formatCurrency(formData.btw_bedrag || 0)}</strong>
            </div>
            <div className="totaal-row totaal-final">
              <span>Totaal:</span>
              <strong>{formatCurrency(formData.totaal || 0)}</strong>
            </div>
          </div>
        </div>

        <div className="form-section winstmarge-section">
          <h3>Winstmarge</h3>
          <div className="winstmarge-boxes" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div className="winstmarge-box">
              <div className="winstmarge-label">Winstmarge op materiaal</div>
              <div className="winstmarge-amount">{formatCurrency(berekenWinstmarges().materiaal)}</div>
            </div>
            <div className="winstmarge-box">
              <div className="winstmarge-label">Winstmarge op uren</div>
              <div className="winstmarge-amount">{formatCurrency(berekenWinstmarges().uren)}</div>
            </div>
            <div className="winstmarge-box">
              <div className="winstmarge-label">Winstmarge op uitbesteed</div>
              <div className="winstmarge-amount">{formatCurrency(berekenWinstmarges().uitbesteed)}</div>
            </div>
            <div className="winstmarge-box" style={{ backgroundColor: '#e8f5e9', borderColor: '#4caf50' }}>
              <div className="winstmarge-label">Totale winstmarge</div>
              <div className="winstmarge-amount">{formatCurrency(berekenWinstmarges().materiaal + berekenWinstmarges().uren + berekenWinstmarges().uitbesteed)}</div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/offertes')}>
            Annuleren
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Opslaan...' : (isEditMode ? 'Offerte Bijwerken' : 'Offerte Aanmaken')}
          </button>
        </div>
      </form>

      {/* Delete Calculatie Bevestigingsmodal */}
      {deleteCalculatieModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1a1a1a' }}>
              Calculatie Verwijderen
            </h3>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Weet je zeker dat je "{deleteCalculatieModal.naam}" wilt verwijderen?<br />
              <strong>Deze actie kan niet ongedaan worden gemaakt.</strong>
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteCalculatieModal({ show: false, index: null, naam: '' })}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Annuleren
              </button>
              <button
                onClick={confirmDeleteCalculatie}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferteForm;